import React, { useEffect, useState } from 'react';
import { Save, Plus, Trash2, ArrowUp, ArrowDown, ChevronDown, ChevronUp } from 'lucide-react';
import { apiCall, fetchAdmin, ImageUploader } from './_helpers';
import SectionEditor from './SectionEditor';

const PAGE_SLUGS = [
  { slug: 'our_story', label: 'Our Story (/our-story)' },
  { slug: 'chefs_table', label: "Chef's Table (/chefs-table)" },
  { slug: 'community', label: 'Community (/community)' },
  { slug: 'private_events', label: 'Private Events (/private-events)' },
  { slug: 'contact', label: 'Contact (/contact)' },
  { slug: 'menu_chaat', label: 'Menu · Chaat (/menu/chaat)' },
  { slug: 'menu_woodfire', label: 'Menu · Woodfire & Tandoor (/menu/woodfire-tandoors)' },
  { slug: 'menu_regions', label: 'Menu · Reimagined Regions (/menu/reimagined-regions)' },
  { slug: 'menu_classics', label: 'Menu · Rustic Classics (/menu/rustic-classics)' },
  { slug: 'menu_flour_water', label: 'Menu · Flour & Water (/menu/flour-water)' },
];

const SECTION_TYPES = [
  { value: 'rich_text', label: 'Rich Text (paragraphs)' },
  { value: 'card_with_image', label: 'Card with Image (chef/partner bio)' },
  { value: 'feature_grid', label: 'Feature Grid (icon cards)' },
  { value: 'two_column', label: 'Two Column (list + details)' },
  { value: 'bullet_list', label: 'Bullet List' },
  { value: 'details_grid', label: 'Details Grid (label/value)' },
  { value: 'cta_button', label: 'CTA Button' },
  { value: 'initiatives_grid', label: 'Initiatives Grid (Community)' },
  { value: 'mission_card', label: 'Mission Card (with CTAs)' },
  { value: 'info_card_grid', label: 'Info Card Grid (event types/venue cards)' },
  { value: 'info_card_grid_packages', label: 'Packages Grid (Private Events)' },
  { value: 'venue_card', label: 'Venue Card' },
  { value: 'inquiry_form', label: 'Inquiry Form' },
  { value: 'contact_block', label: 'Contact Block (auto-pulls Restaurant Info)' },
  { value: 'map_embed', label: 'Map Embed' },
  { value: 'reservation_card', label: 'Reservation Card' },
  { value: 'menu_items_list', label: 'Menu Items List (by category)' },
  { value: 'note', label: 'Note (italic small text)' },
  { value: 'html', label: 'Raw HTML' },
];

const PageManager = ({ onMessage }) => {
  const [activeSlug, setActiveSlug] = useState(PAGE_SLUGS[0].slug);
  const [page, setPage] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    setLoading(true);
    fetchAdmin(`pages/${activeSlug}`).then((data) => {
      setPage(data || { slug: activeSlug, hero: {}, sections: [] });
      setExpandedSections({});
      setLoading(false);
    });
  }, [activeSlug]);

  const updateHero = (patch) => setPage({ ...page, hero: { ...(page.hero || {}), ...patch } });

  const sections = page.sections || [];
  const updateSection = (idx, next) => {
    const list = sections.map((s, i) => (i === idx ? next : s));
    setPage({ ...page, sections: list });
  };
  const addSection = (type) => {
    const newSec = { id: `sec_${Date.now()}`, type, order: sections.length + 1, data: {} };
    setPage({ ...page, sections: [...sections, newSec] });
    setExpandedSections({ ...expandedSections, [newSec.id]: true });
  };
  const removeSection = (idx) => setPage({ ...page, sections: sections.filter((_, i) => i !== idx) });
  const moveSection = (idx, dir) => {
    const next = [...sections];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    next.forEach((s, i) => (s.order = i + 1));
    setPage({ ...page, sections: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = {
        slug: activeSlug,
        hero: page.hero || {},
        sections: (page.sections || []).map((s, i) => ({ ...s, order: i + 1 })),
        extra: page.extra || null,
      };
      await apiCall('PUT', `pages/${activeSlug}`, cleaned);
      onMessage('success', 'Page saved');
    } catch (e) {
      onMessage('error', e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = (id) => setExpandedSections({ ...expandedSections, [id]: !expandedSections[id] });

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-md">
        <h2 className="text-2xl font-bold mb-4">Pages</h2>
        <p className="text-sm text-gray-500 mb-4">Edit hero + sections of each public page. Sections render in order.</p>
        <select value={activeSlug} onChange={(e) => setActiveSlug(e.target.value)} className="w-full border rounded-lg px-4 py-2" data-testid="page-slug-selector">
          {PAGE_SLUGS.map((p) => <option key={p.slug} value={p.slug}>{p.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl p-6 shadow-md text-center text-gray-500">Loading…</div>
      ) : (
        <>
          <div className="bg-white rounded-xl p-6 shadow-md">
            <h3 className="text-lg font-bold mb-4">Hero</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Hero Image</label>
                <ImageUploader currentImage={page.hero?.image_url} onUpload={(url) => updateHero({ image_url: url })} />
                <input type="text" value={page.hero?.image_url || ''} onChange={(e) => updateHero({ image_url: e.target.value })} placeholder="or paste URL" className="w-full border rounded px-3 py-2 mt-2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input type="text" value={page.hero?.title || ''} onChange={(e) => updateHero({ title: e.target.value })} className="w-full border rounded px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Subtitle</label>
                <textarea value={page.hero?.subtitle || ''} onChange={(e) => updateHero({ subtitle: e.target.value })} className="w-full border rounded px-3 py-2" rows={2} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Sections ({sections.length})</h3>
              <div className="flex gap-2">
                <select onChange={(e) => { if (e.target.value) { addSection(e.target.value); e.target.value = ''; } }} className="border rounded-lg px-3 py-2 text-sm" data-testid="add-section-select">
                  <option value="">+ Add Section…</option>
                  {SECTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-3">
              {sections.map((s, idx) => {
                const expanded = expandedSections[s.id];
                return (
                  <div key={s.id || idx} className="border rounded-lg" data-testid={`section-row-${idx}`}>
                    <div className="flex items-center justify-between p-3 bg-gray-50">
                      <div className="flex items-center gap-3">
                        <button onClick={() => toggle(s.id)} className="p-1 hover:bg-gray-200 rounded">
                          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                        <span className="font-semibold text-sm">#{idx + 1} · {SECTION_TYPES.find((t) => t.value === s.type)?.label || s.type}</span>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="p-2 hover:bg-gray-200 rounded disabled:opacity-30"><ArrowUp size={14} /></button>
                        <button onClick={() => moveSection(idx, 1)} disabled={idx === sections.length - 1} className="p-2 hover:bg-gray-200 rounded disabled:opacity-30"><ArrowDown size={14} /></button>
                        <button onClick={() => removeSection(idx)} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                      </div>
                    </div>
                    {expanded && (
                      <div className="p-4 border-t">
                        <SectionEditor section={s} onChange={(next) => updateSection(idx, next)} />
                      </div>
                    )}
                  </div>
                );
              })}
              {sections.length === 0 && <p className="text-gray-500 text-center py-8 text-sm">No sections yet. Use the dropdown above to add one.</p>}
            </div>
          </div>

          <button onClick={save} disabled={saving} className="bg-[#C9A86C] text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-[#B8956A] disabled:opacity-50" data-testid="page-save-btn">
            <Save size={18} /> {saving ? 'Saving…' : `Save ${PAGE_SLUGS.find((p) => p.slug === activeSlug)?.label || 'Page'}`}
          </button>
        </>
      )}
    </div>
  );
};

export default PageManager;
